import { IndustrySlug } from 'containers/Setting/components/RoleFormModal/customerIntelligence';

import { IntelligenceFixture, MetricTone } from '../../types';
import { PLACEHOLDER_CONFIDENCE_REASON } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

/**
 * Read off the LIVE Figma canvas for `589-29081` at 103% and again at 111%, which resolves the label
 * text the 11k-tall frame render cannot. The band is ONE 1360x874 surface (Layout: Width 1,360px,
 * Height 874px, Top 8541.8px, Left 299px), so every panel below belongs to the single card.
 *
 * The figures the card shipped with before this were invented against a 96,140 active base that this
 * node never states. What it states instead is 125,000 evaluated and 38,400 eligible, and those two
 * close the arithmetic on their own: 38,400 / 125,000 = 30.72%, which is the 30.7% the second tile
 * prints.
 */

/** The four readings of the tile strip, left to right, as `589-29081` orders them. */
export const productReadinessCardFixtures: Record<string, IntelligenceFixture> = {
  product_readiness: {
    cardKey: 'product_readiness',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'eligible_customers',
        label: 'Eligible Customers',
        value: 38400,
        format: 'count',
        denominator: 'Previous: 35,200',
        delta: {
          value: 3200,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          // 3,200 on the previous period's 35,200, which is the +9.1% the tile prints beside it.
          secondary: { value: 9.1, unit: 'percent' }
        }
      },
      {
        key: 'eligible_customer_rate',
        label: 'Eligible Customer Rate',
        value: 30.7,
        format: 'percent',
        denominator: 'Previous: 28.8%',
        delta: { value: 1.9, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'product_gap_customers',
        label: 'Product-gap Customers',
        value: 24650,
        format: 'count',
        denominator: 'Existing holding and active product status applied',
        /* `↑1,150 (3.8% pp)`, stated verbatim. Unlike the first tile this one prints no previous
           figure, and 3.8 is neither 1,150 against 23,500 (4.89%) nor against 24,650 (4.67%) — the
           `pp` inside the parentheses is the file naming the form, a percentage POINT movement in
           the slot a relative percentage takes — so it ships as a `pp` secondary rather than being
           recomputed. The same form is on Customer Population, Customer Engagement, Experience &
           Value and Payments. Its sibling tile above is the screen's one true `percent`. */
        delta: {
          value: 1150,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 3.8, unit: 'pp' }
        }
      },
      {
        key: 'total_evaluated',
        label: 'Total Evaluated',
        value: 125000,
        format: 'count',
        // The only tile the node draws with no delta line under its denominator.
        denominator: 'Filtered permitted population'
      }
    ],
    confidence: {
      score: 82,
      band: 'high',
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    // Authored prose on the canvas, not Lorem — unusually for this screen, so it is transcribed
    // rather than replaced with `PLACEHOLDER_WHY_THIS_RESULT`.
    whyThisResult:
      'Readiness improved because strong eligibility, observed need and supporting capacity signals outweighed current policy, consent and data-quality blockers.'
  }
};

/**
 * `MetricTone` plus the one ink it has no member for: `589-29081` paints the Insufficient Data
 * segment GREY, and `TONE_INKS` covers only positive / warning / negative / info / accent. The hex
 * was NOT sampled off the node — the properties panel would not open on this band — so the section
 * paints it with the nearest role in the palette and says so where it maps it.
 */
export type ReadinessBandTone = MetricTone | 'neutral';

export interface ReadinessBand {
  label: string;
  value: number;
  tone: ReadinessBandTone;
}

/**
 * The two populations `4182-9637` draws the card over, side by side. They are the Metric List's
 * `target_product_holding_count` evaluated for the CURRENTLY SELECTED target product:
 * `count = 0` is the acquisition / initial-adoption branch ("No Credit Card" on the frame) and
 * `count >= 1` the existing-holder branch ("Existing Card Holder"). Server-computed per product
 * through the existing `product_family` / `product_code` template tags — deliberately NOT the
 * `productRelationship` filter, which stays a separate population-level control. PLA-8815.
 */
export const HOLDING_BRANCHES = ['noHolding', 'holding'] as const;

export type HoldingBranch = (typeof HOLDING_BRANCHES)[number];

/**
 * The Metric List guardrail, carried in the type rather than in a convention: an input that does not
 * apply to one branch states so. A `0` here would read as "measured at nothing" and an
 * `insufficientData` state as "we tried and failed", and the sheet rules out both.
 */
export const NOT_APPLICABLE = 'notApplicable';

export type BranchReading = number | typeof NOT_APPLICABLE;

export const isMeasured = (reading: BranchReading): reading is number =>
  reading !== NOT_APPLICABLE && Number.isFinite(reading);

export interface ReadinessSignal {
  label: string;
  /**
   * Share observed as a 0-1 fraction per branch; the node prints it as `N% observed` in each of its
   * two value columns. A signal that cannot be read on one side of the split carries
   * `NOT_APPLICABLE` there rather than a zero. PARTIAL because a branch nothing has answered for is
   * neither a reading nor an inapplicability — the column prints `—` for it. PLA-8815.
   */
  observed: Partial<Record<HoldingBranch, BranchReading>>;
  tone: MetricTone;
}

export interface ReadinessDriver {
  label: string;
  subLabel: string;
  /** Score contribution in points, signed as the node draws it. */
  points: number;
}

/**
 * Raw counts and fractions only, per the `[P3-U7]` fixture convention: colour and formatting are the
 * section's. Every share the section draws is taken from these values rather than from the count
 * Figma happens to draw, so a feed with more bands, more signals or fewer drivers still resolves.
 */
export interface ReadinessBranchVisuals {
  readinessDistribution: ReadinessBand[];
  /**
   * The eligible base this branch's ring is drawn over and prints in its hole. Optional: absent
   * until a response has counted it. `sections/productReadinessLive.ts` fills it from the branch's
   * own eligible customers, which is what the governed formula calls Eligible Base.
   */
  eligibleBase?: number;
  /**
   * One reading per tile of the strip, keyed by `CardMetric.key`, so a tile added to or dropped from
   * the card carries its branch lines with it instead of leaving a fourth positional array to keep
   * in step. A key with no entry is a tile nothing has split yet.
   */
  tileReadings: Record<string, BranchReading | undefined>;
}

export interface ProductReadinessVisuals {
  /** The card is drawn twice over, once per side of the target-product holding split. */
  branches: Record<HoldingBranch, ReadinessBranchVisuals>;
  /** One list, two value columns: the signals are shared, their readings are per branch. */
  eligibilitySignals: ReadinessSignal[];
  /** `4182-9637` draws ONE driver and ONE blocker callout for the card, not one per branch. */
  topPositiveDrivers: ReadinessDriver[];
  topBlockers: ReadinessDriver[];
}

/** Cycled so an industry with more signals than the palette has roles still colours every row. */
const SIGNAL_TONES_CYCLE: MetricTone[] = ['positive', 'info', 'warning', 'accent', 'negative'];

/**
 * A signal row: its NAME and its hue, and no reading. The catalogue states WHICH signals a product
 * reads; nothing states how well covered they are. The nine coverage fractions Banking's node draws
 * (72 / 68 / 87 / 62 / 78 / 54 / 83 / 69 / 76%), and the same sequence replayed positionally for
 * the other three industries, apportioned a coverage nobody measured — they are gone, and both
 * value columns print `—` until `eligibility_decision` / `eligibility_reason_codes` reach them.
 * PLA-8815.
 */
const signalsFrom = (labels: string[]): ReadinessSignal[] =>
  labels.map((label, index) => ({
    label,
    observed: {},
    tone: SIGNAL_TONES_CYCLE[index % SIGNAL_TONES_CYCLE.length]
  }));

/**
 * Both sides of the holding split, held open and empty.
 *
 * Every figure this file used to derive was an apportionment of the SAME two Figma numbers — a
 * 38,400 eligible base split 20,000 / 18,400 — re-scaled into five band arcs per branch, into each
 * branch's ring hole and into two sub-lines under every one of the four tiles. The catalogue's own
 * 24 / 31 / 28 / 9 / 8 worked example fed the other three industries the same way. None of it was a
 * measurement.
 *
 * What fills these shells is the `-PR-` payload already reaching the section on
 * `CardMetric.readiness`, resolved by `sections/productReadinessLive.ts`: `propensity_band` groups
 * the arcs weighted by `n_customers`, the branch's eligible customers fill the hole, and
 * `target_product_holding_status` decides which branch a cell falls on. Until a row lands, both
 * branches state nothing and every shell above draws its empty state. PLA-8815.
 */
const emptyBranches = (): Record<HoldingBranch, ReadinessBranchVisuals> => ({
  noHolding: { readinessDistribution: [], tileReadings: {} },
  holding: { readinessDistribution: [], tileReadings: {} }
});

/**
 * The catalogue asks each Product Readiness row for "top positive drivers; top blockers" and never
 * names one; Figma draws `Lorem Ipsum` over six invented contributions (+6.8 / +4.1 / +2.9 and
 * -5.2 / -3.8 / -2.6 pts, three of them under a fabricated customers-affected count). No column in
 * the landed `-PR-` schema is a signed point contribution at all, so both rails keep their titled
 * box over an `emptyText` and carry no rows. `exclusion_reason` and `excluded_from_marketing` could
 * one day name a blocker and count it; the +/- points column has no source. PLA-8815.
 */
const NO_DRIVERS: ReadinessDriver[] = [];

/**
 * Keyed by the PLA-8799 tenant industry slug. `Record<IndustrySlug, ...>` rather than a loose string
 * map on purpose: adding a fifth industry to the frozen PLA-8807 catalog then fails this file to
 * compile instead of silently drawing Banking's signals for it.
 *
 * Banking's signals are the nine `589-29081` draws. The other three industries have NO Figma node —
 * established twice: the 2026-08-12 band inventory records one Product Readiness band per frame, and
 * the new `2533:1670` "Industry specific - insights cards" frame holds only Wallet, Insurance and
 * Telco *Industry Insights* mockups, no Product Readiness card. Their signal names therefore come
 * from the metrics catalogue: the `Supporting:` clause of each industry's recommended-default
 * product row, cited per industry below.
 */
export const PRODUCT_READINESS_VISUALS: Record<IndustrySlug, ProductReadinessVisuals> = {
  banking_bfsi: {
    branches: emptyBranches(),
    /**
     * Nine rows, in the node's own order — the NAMES the card reads, which is catalogue vocabulary
     * on the same footing as the product dropdown. Their per-branch readings are gone; so is the
     * `NOT_APPLICABLE` four of them carried on the acquisition branch, which was an applicability
     * call made off the signal name rather than off the sheet's `Notes for Data/BE` column. Both
     * come back from `eligibility_decision` / `eligibility_reason_codes`. PLA-8815.
     */
    eligibilitySignals: [
      { label: 'Credit Card Holding', observed: {}, tone: 'positive' },
      { label: 'Total Credit Limit', observed: {}, tone: 'info' },
      { label: 'Used Credit Limit', observed: {}, tone: 'warning' },
      { label: 'Available Limit', observed: {}, tone: 'accent' },
      { label: 'Utilization', observed: {}, tone: 'negative' },
      { label: 'Repayment History', observed: {}, tone: 'positive' },
      { label: 'Income', observed: {}, tone: 'positive' },
      { label: 'Affordability', observed: {}, tone: 'info' },
      { label: 'Delinquency', observed: {}, tone: 'warning' }
    ],
    topPositiveDrivers: NO_DRIVERS,
    topBlockers: NO_DRIVERS
  },

  // O-FIN-PR-001 "Wallet Activation / Reactivation Readiness", this industry's recommended default
  // product; seven signals from its `Supporting:` clause, in the catalogue's own order.
  fintech_wallet_payment: {
    branches: emptyBranches(),
    eligibilitySignals: signalsFrom([
      'Wallet Registration',
      'KYC Status',
      'Wallet Balance',
      'Last Activity',
      'App Sessions',
      'Failed Onboarding Steps',
      'Consent & Eligibility'
    ]),
    topPositiveDrivers: NO_DRIVERS,
    topBlockers: NO_DRIVERS
  },

  // O-INS-PR-001 "Policy Renewal Readiness"; eight signals from its `Supporting:` clause.
  insurance: {
    branches: emptyBranches(),
    eligibilitySignals: signalsFrom([
      'Policy Status',
      'Expiry / Renewal Date',
      'Premium Payment History',
      'Lapse Risk',
      'Claims',
      'Engagement',
      'Affordability',
      'Consent & Eligibility'
    ]),
    topPositiveDrivers: NO_DRIVERS,
    topBlockers: NO_DRIVERS
  },

  // O-TEL-PR-001 "Mobile Plan Upgrade Readiness"; nine signals from its `Supporting:` clause.
  telco: {
    branches: emptyBranches(),
    eligibilitySignals: signalsFrom([
      'Current Plan',
      'Monthly Spend / ARPU',
      'Usage vs Allowance',
      'Overage',
      'Recharge / Payment History',
      'Tenure',
      'Service Quality',
      'Complaints',
      'Eligibility'
    ]),
    topPositiveDrivers: NO_DRIVERS,
    topBlockers: NO_DRIVERS
  }
};

/**
 * `hasOwnProperty.call` over this file's own record rather than `isIndustrySlug`: it keeps fixtures
 * free of a runtime import from `registry/`, and `in` would walk the prototype chain so a tenant
 * industry of 'constructor' would resolve to a function
 * (`conventions/components/cross-browser.md § 9` rules out `Object.hasOwn`).
 *
 * A tenant whose industry is unset or unrecognised keeps the shipped Banking reading, because this
 * card is deliberately NOT industry-gated (`registry/cards.ts` PLA-8807 note) and so still renders
 * for it. Whether it should instead read Not Configured is a product question, raised with the user
 * rather than decided here.
 */
export const visualsForIndustry = (industry?: string | null): ProductReadinessVisuals =>
  industry && Object.prototype.hasOwnProperty.call(PRODUCT_READINESS_VISUALS, industry)
    ? PRODUCT_READINESS_VISUALS[industry as IndustrySlug]
    : PRODUCT_READINESS_VISUALS.banking_bfsi;

/** The shipped Banking reading, kept as a named export so existing consumers resolve unchanged. */
export const productReadinessVisuals = PRODUCT_READINESS_VISUALS.banking_bfsi;
